import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';
import {
  createPhoneModel,
  getAdminPhoneModels,
  getBrands,
  getColors,
  updatePhoneModel,
} from '@/features/operations/api';
import {
  ModelColorsField,
  type ColorSource,
} from '@/features/operations/components/ModelColorsField';
import type { AdminPhoneModelItem, BrandItem, ColorItem } from '@/features/operations/types';

type ModelDraft = { model: string; storageOptions: string; colorIds: string[] };
type ModelDrafts = Record<string, ModelDraft>;

function formatStorageOptions(storageOptions: number[]) {
  return storageOptions.join(', ');
}

function buildDraft(phoneModel: AdminPhoneModelItem): ModelDraft {
  return {
    model: phoneModel.model,
    storageOptions: formatStorageOptions(phoneModel.storageOptions),
    // Older backends answered without colours; treat that as none.
    colorIds: (phoneModel.colors ?? []).map((color) => color.id),
  };
}

function sameColorIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((colorId) => right.includes(colorId));
}

/// The other models, as "copy colours from" lists them: only those that have colours.
function colorSources(phoneModels: AdminPhoneModelItem[], exceptId?: string): ColorSource[] {
  return phoneModels
    .filter((phoneModel) => phoneModel.id !== exceptId && (phoneModel.colors ?? []).length > 0)
    .map((phoneModel) => ({
      id: phoneModel.id,
      label: `${phoneModel.brand.name} ${phoneModel.model}`,
      colorIds: phoneModel.colors.map((color) => color.id),
    }));
}

function buildDrafts(phoneModels: AdminPhoneModelItem[]): ModelDrafts {
  return phoneModels.reduce<ModelDrafts>((drafts, phoneModel) => {
    drafts[phoneModel.id] = buildDraft(phoneModel);
    return drafts;
  }, {});
}

function parseStorageOptions(value: string): number[] {
  const tokens = value
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length === 0) throw new Error('Agregá al menos una capacidad.');
  if (tokens.some((token) => !/^\d+$/.test(token))) {
    throw new Error('Las capacidades deben ser enteros positivos separados por coma.');
  }

  const storageOptions = tokens.map(Number);
  if (storageOptions.some((option) => !Number.isSafeInteger(option) || option <= 0)) {
    throw new Error('Las capacidades deben ser enteros positivos separados por coma.');
  }
  if (new Set(storageOptions).size !== storageOptions.length) {
    throw new Error('No repitas capacidades para el mismo modelo.');
  }

  return storageOptions.sort((left, right) => left - right);
}

function sameStorageOptions(left: number[], right: number[]) {
  return left.length === right.length && left.every((option, index) => option === right[index]);
}

function visibility(phoneModel: AdminPhoneModelItem) {
  if (!phoneModel.isActive) return { label: 'Retirado', color: 'default' as const };
  if (!phoneModel.brand.isActive) {
    return { label: 'Oculto por marca', color: 'warning' as const };
  }
  return { label: 'Se ofrece', color: 'success' as const };
}

function describeConsequence(phoneModel: AdminPhoneModelItem) {
  if (!phoneModel.isActive) {
    return phoneModel.brand.isActive
      ? 'Al reactivarlo vuelve a los formularios web y presencial.'
      : 'Aunque lo reactives seguirá oculto hasta que la marca vuelva a ofrecerse.';
  }
  if (!phoneModel.brand.isActive) {
    return 'El modelo está activo, pero la marca retirada lo mantiene fuera de los formularios.';
  }
  return 'Si lo desactivás sale de ambos formularios. Las solicitudes y precios no se borran.';
}

function describeStatusChange(entry: AdminPhoneModelItem['statusHistory'][number]) {
  if (entry.fromIsActive === null) return 'Modelo creado activo';
  return entry.toIsActive ? 'Modelo reactivado' : 'Modelo desactivado';
}

export function AdminPhoneModelsPage() {
  const { token } = useAuth();
  const [phoneModels, setPhoneModels] = useState<AdminPhoneModelItem[]>([]);
  const [brands, setBrands] = useState<BrandItem[]>([]);
  const [colors, setColors] = useState<ColorItem[]>([]);
  const [drafts, setDrafts] = useState<ModelDrafts>({});
  const [newBrandId, setNewBrandId] = useState('');
  const [newModel, setNewModel] = useState('');
  const [newStorageOptions, setNewStorageOptions] = useState('');
  const [newColorIds, setNewColorIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingDeactivation, setPendingDeactivation] = useState<AdminPhoneModelItem | null>(null);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [deactivationReasonError, setDeactivationReasonError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [loadedModels, loadedBrands, loadedColors] = await Promise.all([
          getAdminPhoneModels(token),
          getBrands(token),
          getColors(token),
        ]);
        setPhoneModels(loadedModels);
        setDrafts(buildDrafts(loadedModels));
        setBrands(loadedBrands);
        setColors(loadedColors);
        const defaultBrand =
          loadedBrands.find((brand) => brand.isActive) || loadedBrands[0] || null;
        setNewBrandId((current) => current || defaultBrand?.id || '');
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'No pudimos cargar los modelos.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [token]);

  if (!token) return null;

  /// Edits one field of a model's card, starting from what is saved if the card was untouched.
  const patchDraft = (phoneModel: AdminPhoneModelItem, patch: Partial<ModelDraft>) =>
    setDrafts((current) => ({
      ...current,
      [phoneModel.id]: { ...(current[phoneModel.id] ?? buildDraft(phoneModel)), ...patch },
    }));

  const refresh = async () => {
    const loaded = await getAdminPhoneModels(token);
    setPhoneModels(loaded);
    setDrafts(buildDrafts(loaded));
  };

  const run = async (action: () => Promise<void>, successText: string) => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await action();
      setSuccessMessage(successText);
      return true;
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos guardar los cambios.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = () =>
    run(async () => {
      if (!newBrandId) throw new Error('Elegí una marca.');
      if (!newModel.trim()) throw new Error('Poné el nombre del modelo.');
      await createPhoneModel(token, {
        brandId: newBrandId,
        model: newModel.trim(),
        storageOptions: parseStorageOptions(newStorageOptions),
        colorIds: newColorIds,
      });
      setNewModel('');
      setNewStorageOptions('');
      setNewColorIds([]);
      await refresh();
    }, 'Modelo creado. Ya está disponible si su marca está activa.');

  const handleSave = (phoneModel: AdminPhoneModelItem) =>
    run(async () => {
      const draft = drafts[phoneModel.id];
      const model = (draft?.model || '').trim();
      if (!model) throw new Error('El nombre no puede quedar vacío.');

      const storageOptions = parseStorageOptions(draft?.storageOptions || '');
      const colorIds = draft?.colorIds ?? [];
      const input: { model?: string; storageOptions?: number[]; colorIds?: string[] } = {};
      if (model !== phoneModel.model) input.model = model;
      if (!sameStorageOptions(storageOptions, phoneModel.storageOptions)) {
        input.storageOptions = storageOptions;
      }
      if (
        !sameColorIds(
          colorIds,
          (phoneModel.colors ?? []).map((color) => color.id),
        )
      ) {
        input.colorIds = colorIds;
      }
      if (!input.model && !input.storageOptions && !input.colorIds) {
        throw new Error('El modelo no cambió.');
      }

      await updatePhoneModel(token, phoneModel.id, input);
      await refresh();
    }, 'Modelo actualizado.');

  const handleToggleActive = (phoneModel: AdminPhoneModelItem) => {
    if (phoneModel.isActive) {
      setPendingDeactivation(phoneModel);
      setDeactivationReason('');
      setDeactivationReasonError(null);
      return;
    }

    void run(async () => {
      await updatePhoneModel(token, phoneModel.id, { isActive: true });
      await refresh();
    }, 'Modelo reactivado.');
  };

  const handleConfirmDeactivation = async () => {
    if (!pendingDeactivation) return;
    const reason = deactivationReason.trim();
    if (!reason) {
      setDeactivationReasonError('Indicá por qué PhoneTec deja de aceptar este modelo.');
      return;
    }

    const saved = await run(async () => {
      await updatePhoneModel(token, pendingDeactivation.id, { isActive: false, reason });
      await refresh();
    }, 'Modelo desactivado y motivo registrado.');

    if (saved) {
      setPendingDeactivation(null);
      setDeactivationReason('');
      setDeactivationReasonError(null);
    }
  };

  const closeDeactivationDialog = () => {
    if (isSaving) return;
    setPendingDeactivation(null);
    setDeactivationReason('');
    setDeactivationReasonError(null);
  };

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="CONFIGURACIÓN COMERCIAL"
        title="Modelos del catálogo"
        description="Qué celulares y capacidades acepta PhoneTec en las cotizaciones web y presenciales. Los modelos se retiran sin borrar su historia ni sus precios."
        badge="Solo administración"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          <SectionCard
            title="Agregar un modelo"
            description="Se crea activo. Si la marca también está activa, aparece inmediatamente en ambos formularios de cotización."
          >
            {brands.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Primero cargá una marca en{' '}
                <Link component={RouterLink} to="/admin/brands">
                  Marcas del catálogo
                </Link>
                .
              </Alert>
            ) : null}
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Marca"
                  value={newBrandId}
                  onChange={(event) => setNewBrandId(event.target.value)}
                  disabled={isSaving || brands.length === 0}
                >
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                      {brand.isActive ? '' : ' (retirada)'}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Modelo"
                  value={newModel}
                  onChange={(event) => setNewModel(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Capacidades (GB)"
                  placeholder="128, 256, 512"
                  value={newStorageOptions}
                  onChange={(event) => setNewStorageOptions(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PrimaryButton
                  fullWidth
                  onClick={handleCreate}
                  disabled={isSaving || brands.length === 0}
                  sx={{ minHeight: 56 }}
                >
                  Agregar modelo
                </PrimaryButton>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Escribí las capacidades como enteros positivos separados por coma, por ejemplo:
                  128, 256, 512.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ModelColorsField
                  label="Colores del modelo"
                  colors={colors}
                  value={newColorIds}
                  onChange={setNewColorIds}
                  sources={colorSources(phoneModels)}
                  disabled={isSaving}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="Modelos registrados"
            description="La marca no se puede cambiar: moverla trasladaría también su configuración de precios. Para retirarlo, desactivalo y registrá el motivo."
          >
            {phoneModels.length === 0 ? (
              <Alert severity="info">
                Todavía no hay modelos. Sin al menos uno activo bajo una marca activa no se pueden
                registrar cotizaciones.
              </Alert>
            ) : (
              <Stack spacing={2.5}>
                {phoneModels.map((phoneModel) => {
                  const state = visibility(phoneModel);
                  // Keep the screen usable while a development tab or backend process still
                  // holds the response shape from before pricing/audit metadata was added.
                  const pricedStorageOptions = phoneModel.pricedStorageOptions ?? [];
                  const statusHistory = phoneModel.statusHistory ?? [];
                  return (
                    <Box
                      key={phoneModel.id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: { xs: 2, md: 2.5 },
                      }}
                    >
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={2}>
                          <Stack spacing={0.75} sx={{ minHeight: 56, justifyContent: 'center' }}>
                            <Typography variant="overline" color="text.secondary" lineHeight={1}>
                              Marca
                            </Typography>
                            <Typography variant="body1" fontWeight={700}>
                              {phoneModel.brand.name}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Chip label={state.label} color={state.color} size="small" />
                              <Chip
                                label={
                                  phoneModel.isQuotable ? 'Cálculo habilitado' : 'Cálculo pausado'
                                }
                                variant="outlined"
                                size="small"
                              />
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label={`Modelo de ${phoneModel.brand.name}`}
                            value={drafts[phoneModel.id]?.model ?? ''}
                            onChange={(event) =>
                              patchDraft(phoneModel, { model: event.target.value })
                            }
                            disabled={isSaving}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label={`Capacidades de ${phoneModel.model}`}
                            value={drafts[phoneModel.id]?.storageOptions ?? ''}
                            onChange={(event) =>
                              patchDraft(phoneModel, { storageOptions: event.target.value })
                            }
                            disabled={isSaving}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            alignItems="center"
                            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                            sx={{ minHeight: 56 }}
                          >
                            <PrimaryButton
                              onClick={() => handleSave(phoneModel)}
                              disabled={isSaving}
                            >
                              Guardar
                            </PrimaryButton>
                            <PrimaryButton
                              color="secondary"
                              onClick={() => handleToggleActive(phoneModel)}
                              disabled={isSaving}
                            >
                              {phoneModel.isActive ? 'Desactivar' : 'Reactivar'}
                            </PrimaryButton>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          {/* Saved with the rest of the card, by "Guardar". */}
                          <ModelColorsField
                            label={`Colores de ${phoneModel.model}`}
                            colors={colors}
                            value={drafts[phoneModel.id]?.colorIds ?? []}
                            onChange={(colorIds) => patchDraft(phoneModel, { colorIds })}
                            currentColors={phoneModel.colors ?? []}
                            sources={colorSources(phoneModels, phoneModel.id)}
                            disabled={isSaving}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 2, py: 1.5 }}>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" color="text.secondary">
                                {pricedStorageOptions.length
                                  ? `Capacidades con precio: ${formatStorageOptions(pricedStorageOptions)} GB. No se pueden retirar hasta resolver esos precios.`
                                  : 'Ninguna capacidad tiene precio base todavía.'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {describeConsequence(phoneModel)} Última modificación:{' '}
                                {new Date(phoneModel.updatedAt).toLocaleString('es-UY')}
                                {phoneModel.updatedBy
                                  ? ` por ${phoneModel.updatedBy.fullName}`
                                  : ''}
                                . Los montos se administran en{' '}
                                <Link component={RouterLink} to="/admin/pricing">
                                  Configuración comercial
                                </Link>
                                .
                              </Typography>
                            </Stack>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box
                            component="details"
                            sx={{
                              '& summary': { cursor: 'pointer' },
                              '&[open] summary': { mb: 1 },
                            }}
                          >
                            <Typography component="summary" variant="body2" fontWeight={700}>
                              Historial de estado ({statusHistory.length})
                            </Typography>
                            {statusHistory.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No hay transiciones registradas para este modelo anteriores a la
                                incorporación del historial.
                              </Typography>
                            ) : (
                              <Stack spacing={1}>
                                {statusHistory.map((entry) => (
                                  <Box key={entry.id}>
                                    <Typography variant="body2">
                                      {describeStatusChange(entry)} ·{' '}
                                      {new Date(entry.createdAt).toLocaleString('es-UY')} ·{' '}
                                      {entry.changedBy?.fullName || 'Actor no disponible'}
                                    </Typography>
                                    {entry.reason ? (
                                      <Typography variant="caption" color="text.secondary">
                                        Motivo: {entry.reason}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </SectionCard>
        </>
      )}

      <Dialog
        open={Boolean(pendingDeactivation)}
        onClose={closeDeactivationDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Desactivar {pendingDeactivation?.model}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            El modelo dejará de aparecer en las cotizaciones web y presenciales. Las solicitudes
            históricas, precios y descuentos se conservarán.
          </DialogContentText>
          <TextField
            autoFocus
            required
            fullWidth
            multiline
            minRows={3}
            label="Motivo de desactivación"
            value={deactivationReason}
            onChange={(event) => {
              setDeactivationReason(event.target.value);
              setDeactivationReasonError(null);
            }}
            error={Boolean(deactivationReasonError)}
            helperText={
              deactivationReasonError ||
              'Quedará registrado junto con tu usuario y la fecha del cambio.'
            }
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivationDialog} disabled={isSaving}>
            Cancelar
          </Button>
          <PrimaryButton onClick={() => void handleConfirmDeactivation()} disabled={isSaving}>
            Confirmar desactivación
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
